import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaCheck, 
  FaTimes, 
  FaEnvelope, 
  FaPhone, 
  FaUser,
  FaClock,
  FaSpinner,
  FaExchangeAlt
} from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function PendingApprovals() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null); // Can be requestId string or { requestId, service } object

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', '12');
    return `/api/admin/trainers/pending?${params.toString()}`;
  };

  const { data, error, mutate } = useSWR(buildQuery(), Fetcher);
  const requests = data?.data || [];
  const pagination = data?.pagination || { total: 0, pages: 1 };

  const handleApprove = async (request) => {
    setLoading(request._id);
    try {
      if (request.requestType === 'specialization_change') {
        await HttpInterceptor.post(`/api/admin/specialization-requests/${request._id}/approve`);
        toast.success('Specialization change approved successfully');
      } else {
        await HttpInterceptor.post(`/api/admin/trainers/${request._id}/approve`);
        toast.success('Trainer approved successfully');
      }
      // Reset to page 1 if current page only had this item
      if (requests.length === 1 && page > 1) {
        setPage(1);
      } else {
        mutate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setLoading(null);
    }
  };

  const handleApproveService = async (request, service) => {
    setLoading(`${request._id}-${service}`);
    try {
      await HttpInterceptor.post(`/api/admin/trainers/${request._id}/services/${encodeURIComponent(service)}/approve`);
      toast.success(`Service "${service}" approved successfully`);
      mutate();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to approve service "${service}"`);
    } finally {
      setLoading(null);
    }
  };

  const handleReject = async (request, service = null) => {
    const loadingKey = service ? `${request._id}-${service}` : request._id;
    setLoading(loadingKey);
    try {
      if (request.requestType === 'specialization_change') {
        await HttpInterceptor.post(`/api/admin/specialization-requests/${request._id}/reject`);
        toast.success('Specialization change request rejected');
      } else if (service) {
        // Service-specific rejection
        await HttpInterceptor.post(`/api/admin/trainers/${request._id}/services/${encodeURIComponent(service)}/reject`, {
          reason: rejectReason
        });
        toast.success(`Service "${service}" rejected`);
      } else {
        // Full application rejection
        await HttpInterceptor.post(`/api/admin/trainers/${request._id}/reject`, {
          reason: rejectReason
        });
        toast.success('Trainer rejected successfully');
      }
      setShowRejectModal(null);
      setRejectReason('');
      // Reset to page 1 if current page only had this item
      if (requests.length === 1 && page > 1) {
        setPage(1);
      } else {
        mutate();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setLoading(null);
    }
  };

  if (error) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load pending approvals</p>
          <button
            onClick={() => mutate()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Pending Approvals</h1>
          <p className="text-gray-600">Review and approve business partner applications and specialization change requests</p>
        </div>

        {requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FaCheck className="text-6xl text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Caught Up!</h2>
            <p className="text-gray-600">There are no pending approval requests at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map((request) => {
              const isSpecializationChange = request.requestType === 'specialization_change';
              return (
              <div key={request._id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 ${isSpecializationChange ? 'bg-orange-100' : 'bg-purple-100'} rounded-full flex items-center justify-center mr-3`}>
                      {isSpecializationChange ? (
                        <FaExchangeAlt className="text-orange-600 text-xl" />
                      ) : (
                        <FaUser className="text-purple-600 text-xl" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 capitalize">{request.name}</h3>
                      <p className="text-sm text-gray-600">
                        {isSpecializationChange ? 'Specialization Change' : (
                          request.services && request.services.length > 0 
                            ? `${request.services.length} ${request.services.length === 1 ? 'Service' : 'Services'}`
                            : request.service || 'Business Partner'
                        )}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 ${isSpecializationChange ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'} text-xs font-medium rounded-full`}>
                    <FaClock className="inline mr-1" />
                    {isSpecializationChange ? 'Change Request' : 'Pending'}
                  </span>
                </div>

                {isSpecializationChange && (
                  <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500 mb-4">
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaExchangeAlt className="mr-2 text-blue-500" />
                      Specialization Change Request
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">Current:</span> {request.service}
                    </div>
                    <div className="text-lg font-bold text-blue-700">
                      <span className="font-medium">Requested:</span> {request.serviceChangeRequest}
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <FaEnvelope className="mr-2 text-gray-400" />
                    <span className="truncate">{request.email}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaPhone className="mr-2 text-gray-400" />
                    <span>{request.mobile}</span>
                  </div>
                  {!isSpecializationChange && (
                    <>
                      <div className="flex items-center text-sm text-gray-600">
                        <FaUser className="mr-2 text-gray-400" />
                        <span className="capitalize">{request.gender}</span>
                      </div>
                      
                      {/* Services List - Show all services with their partner types */}
                      {request.services && request.services.length > 0 ? (
                        <div className="mt-3 space-y-3">
                          {request.services.map((service, serviceIdx) => {
                            const partnerType = request.servicePartnerTypes?.[service] || request.partnerType || 'instructor';
                            const serviceDetail = request.serviceDetails?.[service] || {};
                            const isConsultant = partnerType === 'consultant';
                            const serviceStatus = request.serviceApprovalStatus?.[service] || 'pending';
                            const serviceRejectionReason = request.serviceRejectionReasons?.[service];
                            
                            return (
                              <div key={serviceIdx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">{service}</h4>
                                    {serviceStatus === 'approved' && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                                        Approved
                                      </span>
                                    )}
                                    {serviceStatus === 'rejected' && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                        Rejected
                                      </span>
                                    )}
                                  </div>
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    isConsultant 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {isConsultant ? 'Consultant' : 'Instructor'}
                                  </span>
                                </div>
                                
                                {serviceRejectionReason && (
                                  <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                    <strong>Rejection Reason:</strong> {serviceRejectionReason}
                                  </div>
                                )}
                                
                                {/* Service-specific details */}
                                <div className="space-y-1.5 text-xs text-gray-600">
                                  <div>
                                    <span className="font-medium">Experience:</span>{' '}
                                    {serviceDetail.yearsOfExperience !== undefined && serviceDetail.yearsOfExperience !== null
                                      ? `${serviceDetail.yearsOfExperience} ${serviceDetail.yearsOfExperience === 1 ? 'year' : 'years'}`
                                      : request.yearsOfExperience !== undefined && request.yearsOfExperience !== null
                                        ? `${request.yearsOfExperience} ${request.yearsOfExperience === 1 ? 'year' : 'years'}`
                                        : <span className="text-gray-400 italic">Not provided</span>
                                    }
                                  </div>
                                  
                                  <div>
                                    <span className="font-medium">Certification:</span>{' '}
                                    {serviceDetail.certification ? (
                                      <span>{serviceDetail.certification}</span>
                                    ) : request.certification ? (
                                      <span>{request.certification}</span>
                                    ) : (
                                      <span className="text-gray-400 italic">Not provided</span>
                                    )}
                                  </div>
                                  
                                  {serviceDetail.bio && (
                                    <div>
                                      <span className="font-medium">Bio:</span>{' '}
                                      <span className="line-clamp-2">{serviceDetail.bio}</span>
                                    </div>
                                  )}
                                  
                                  {serviceDetail.teachingStyle && (
                                    <div>
                                      <span className="font-medium">
                                        {isConsultant ? 'Consultation Approach:' : 'Teaching Style:'}
                                      </span>{' '}
                                      <span>{serviceDetail.teachingStyle}</span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Service-specific approve/reject buttons */}
                                {serviceStatus === 'pending' && (
                                  <div className="flex space-x-2 mt-3 pt-3 border-t border-gray-300">
                                    <button
                                      onClick={() => handleApproveService(request, service)}
                                      disabled={loading === `${request._id}-${service}`}
                                      className="flex-1 bg-green-600 text-white px-3 py-1.5 rounded text-xs hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                      {loading === `${request._id}-${service}` ? (
                                        <FaSpinner className="animate-spin" />
                                      ) : (
                                        <>
                                          <FaCheck className="mr-1" />
                                          Approve
                                        </>
                                      )}
                                    </button>
                                    <button
                                      onClick={() => setShowRejectModal({ requestId: request._id, service })}
                                      disabled={loading === `${request._id}-${service}`}
                                      className="flex-1 bg-red-600 text-white px-3 py-1.5 rounded text-xs hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                      <FaTimes className="mr-1" />
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Fallback for single service (backward compatibility) */
                        <>
                          {/* Experience - Always show */}
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Experience:</span>{' '}
                            {request.yearsOfExperience !== undefined && request.yearsOfExperience !== null 
                              ? `${request.yearsOfExperience} ${request.yearsOfExperience === 1 ? 'year' : 'years'}`
                              : <span className="text-gray-400 italic">Not provided</span>
                            }
                          </div>
                          
                          {/* Certification - Always show */}
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Certification:</span>{' '}
                            {request.certification ? (
                              <span>{request.certification}</span>
                            ) : (
                              <span className="text-gray-400 italic">Not provided</span>
                            )}
                          </div>
                          
                          {/* Bio - Always show */}
                          <div className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">Bio:</span>
                            {request.bio ? (
                              <p className="mt-1 line-clamp-3">{request.bio}</p>
                            ) : (
                              <p className="mt-1 text-gray-400 italic">Not provided</p>
                            )}
                          </div>
                          
                          {/* Teaching Style - Always show */}
                          <div className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">
                              {request.partnerType === 'consultant' ? 'Consultation Approach:' : 'Teaching Style:'}
                            </span>
                            {request.teachingStyle ? (
                              <p className="mt-1">{request.teachingStyle}</p>
                            ) : (
                              <p className="mt-1 text-gray-400 italic">Not provided</p>
                            )}
                          </div>
                        </>
                      )}
                      
                    </>
                  )}
                      
                      {/* Achievements - Show if provided (shared across services) */}
                      {request.achievements && request.achievements.length > 0 && (
                        <div className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Achievements:</span>
                          <ul className="mt-1 list-disc list-inside space-y-1">
                            {request.achievements.map((achievement, idx) => (
                              <li key={idx} className="text-xs">{achievement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                </div>


                {/* Reject Modal */}
                {(showRejectModal === request._id || 
                  (typeof showRejectModal === 'object' && showRejectModal?.requestId === request._id)) && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setShowRejectModal(null);
                      setRejectReason('');
                    }
                  }}>
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">
                        {isSpecializationChange ? 'Reject Specialization Change' : 
                         (typeof showRejectModal === 'object' && showRejectModal?.service) 
                           ? `Reject Service: ${showRejectModal.service}` 
                           : 'Reject Application'}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {isSpecializationChange ? (
                          <>
                            Are you sure you want to reject {request.name}'s specialization change request from <strong>{request.service}</strong> to <strong>{request.serviceChangeRequest}</strong>?
                          </>
                        ) : (typeof showRejectModal === 'object' && showRejectModal?.service) ? (
                          <>
                            Are you sure you want to reject {request.name}'s service: <strong>{showRejectModal.service}</strong>?
                          </>
                        ) : (
                          <>
                            Are you sure you want to reject {request.name}'s entire application?
                          </>
                        )}
                      </p>
                      {!isSpecializationChange && (
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Optional: Provide a reason for rejection"
                          className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          rows="3"
                        />
                      )}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setShowRejectModal(null);
                            setRejectReason('');
                          }}
                          className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            const service = typeof showRejectModal === 'object' ? showRejectModal.service : null;
                            handleReject(request, service);
                          }}
                          disabled={loading === request._id || (typeof showRejectModal === 'object' && loading === `${request._id}-${showRejectModal?.service}`)}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {(loading === request._id || (typeof showRejectModal === 'object' && loading === `${request._id}-${showRejectModal?.service}`)) ? (
                            <FaSpinner className="animate-spin mx-auto" />
                          ) : (
                            'Reject'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-6">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
            <span className="text-gray-600 px-4">
              Page {page} of {pagination.pages} ({pagination.total} total)
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.pages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PendingApprovals;

